import React from "react";

import AnthropicIcon from "src/assets/AnthropicIcon.svg";
import GoogleVertexIcon from "src/assets/GoogleVertexIcon.svg";
import OpenAIIcon from "src/assets/OpenAIIcon.svg";
import { ModelProvider } from "src/clients/agent-management";

/**
 * Maps provider to local icon assets.
 * Used as fallback when iconUrl is not provided.
 */
const PROVIDER_ICON_ASSETS: Partial<Record<ModelProvider, string>> = {
  openai: OpenAIIcon,
  bedrock: AnthropicIcon,
  google_vertex: GoogleVertexIcon,
  anthropic: AnthropicIcon,
};

type ModelIconProps = {
  /** URL for the icon image */
  iconUrl?: string;
  /** React component/node to render as icon (takes precedence over iconUrl) */
  icon?: React.ReactNode;
  /** Provider - used for fallback icon lookup when iconUrl not provided */
  provider: ModelProvider;
  /** Model name for the alt text and fallback letter */
  modelName: string;
};

/**
 * Renders a model icon with flexible input options:
 * 1. If `icon` (React node) is provided, renders it directly
 * 2. Else if `iconUrl` is provided, renders an image from that URL
 * 3. Else looks up icon by `provider` in PROVIDER_ICON_ASSETS
 * 4. Falls back to showing the first letter of the model name
 */
export const ModelIcon: React.FC<ModelIconProps> = ({
  iconUrl,
  icon,
  provider,
  modelName,
}) => {
  // Priority 1: Render provided React icon component
  if (icon) {
    return (
      <div className="box-border flex h-5 w-5 items-center justify-center rounded-[4px] border border-gray-200">
        {icon}
      </div>
    );
  }

  // Priority 2: Render iconUrl if provided
  if (iconUrl) {
    return (
      <div className="box-border h-5 w-5 rounded-[4px] border border-gray-200">
        <img
          alt={`${modelName} Icon`}
          className="h-full w-full"
          src={iconUrl}
        />
      </div>
    );
  }

  // Priority 3: Look up icon by provider
  const providerIcon = PROVIDER_ICON_ASSETS[provider];
  if (providerIcon) {
    return (
      <div className="box-border h-5 w-5 rounded-[4px] border border-gray-200">
        <img
          alt={`${modelName} Icon`}
          className="h-full w-full"
          src={providerIcon}
        />
      </div>
    );
  }

  // Fallback: First letter of model name
  return (
    <div className="flex h-5 w-5 items-center justify-center rounded-[4px] border border-gray-200 bg-gray-100">
      <span className="font-inter-semibold-10px text-gray-500">
        {modelName.charAt(0).toUpperCase()}
      </span>
    </div>
  );
};
